import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const idInput = z.object({ analysisId: z.string().uuid() });
const tokenInput = z.object({ token: z.string().min(8).max(64) });

function genToken() {
  // 32 url-safe chars
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export const enableShare = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => idInput.parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;

    const { data: existing } = await supabaseAdmin
      .from("analyses")
      .select("id, user_id, share_token")
      .eq("id", data.analysisId)
      .maybeSingle();

    if (!existing || existing.user_id !== userId) {
      return { ok: false as const, reason: "not_found" as const };
    }
    if (existing.share_token) {
      return { ok: true as const, token: existing.share_token };
    }

    const token = genToken();
    const { error } = await supabaseAdmin
      .from("analyses")
      .update({ share_token: token, shared_at: new Date().toISOString() })
      .eq("id", data.analysisId);

    if (error) return { ok: false as const, reason: "db_error" as const };
    return { ok: true as const, token };
  });

export const getSharedAnalysis = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => tokenInput.parse(input))
  .handler(async ({ data }) => {
    const { data: row } = await supabaseAdmin
      .from("analyses")
      .select("id, title, tool_slug, risk_level, risk_score, result, created_at, shared_at")
      .eq("share_token", data.token)
      .maybeSingle();

    if (!row) return { ok: false as const, reason: "not_found" as const };
    return { ok: true as const, analysis: row };
  });
